import { isArdoqError } from '@ardoq/common-helpers';
import { confirm, modal, ModalSize, ModalTemplate, prompt } from '@ardoq/modal';
import { CopyModalProps } from './types';
import { trackEvent } from 'tracking/tracking';
import { ArdoqId, AssetType } from '@ardoq/api-types';
import { showToast, ToastType } from '@ardoq/status-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { ButtonType, SecondaryButton } from '@ardoq/button';
import { ButtonWithDropdown, DropdownOptionType } from '@ardoq/dropdown-menu';
import { Inline } from '@ardoq/layout';
import type { WorkspaceNavigationOption } from 'components/AssetsBrowser2024/workspaceNavigationOptions';

export const copyModal = async ({
  entityName,
  copyEntity,
  entityType,
  openCopy,
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders prompt() body once and does not re-render on locale change
  // default arg for legacy callers that haven't been migrated to thread locale.
  i18nLocale = i18nLocale$.state,
}: CopyModalProps & { i18nLocale?: I18nLocale }) => {
  // we don't expose the term "traversal" to the user
  const displayEntityType =
    entityType === AssetType.TRAVERSAL ? 'viewpoint' : entityType;
  const copyName = await prompt({
    title: t(
      i18nLocale,
      'assetsBrowser:modals.copy.title',
      { entityType: displayEntityType },
      'Copy {{entityType}}'
    ),
    initialInputValue: t(
      i18nLocale,
      'assetsBrowser:modals.copy.initialName',
      { entityName },
      '{{entityName}} (copy)'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.copy.subtitle',
      { entityName },
      'Are you sure you want to copy "{{entityName}}"?'
    ),
    inputLabel: t(
      i18nLocale,
      'assetsBrowser:modals.copy.inputLabel',
      'New name'
    ),
    cancelButtonClickId: 'cancel-create-copy',
    confirmButtonClickId: 'confirm-create-copy',
    textInputId: 'copy-name-input',
    confirmButtonTitle: t(
      i18nLocale,
      'assetsBrowser:modals.copy.confirmButton',
      'Create copy'
    ),
  });
  if (copyName) {
    trackEvent('Copy modal: confirmed copy asset', { assetType: entityType });
    const newCopy = await copyEntity(copyName);
    if (isArdoqError(newCopy) || newCopy._id === 'error') {
      return showToast(
        t(
          i18nLocale,
          'assetsBrowser:modals.copy.failedToast',
          { entityType },
          'Copying {{entityType}} failed'
        ),
        ToastType.INFO
      );
    }
    const copyId = newCopy._id;
    openCopiedEntityModal({
      entityType,
      copyName,
      copyId,
      openCopy,
      i18nLocale,
    });
  }
};

type OpenCopiedEntityModal = {
  entityType: string;
  copyName: string;
  copyId: ArdoqId;
  openCopy: (copyId: ArdoqId) => void;
  navigationOptions?: WorkspaceNavigationOption[];
  i18nLocale?: I18nLocale;
};

export const openCopiedEntityModal = ({
  entityType,
  copyName,
  copyId,
  openCopy,
  navigationOptions,
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  // default arg for legacy callers that haven't been migrated to thread locale.
  i18nLocale = i18nLocale$.state,
}: OpenCopiedEntityModal) => {
  // we don't expose the term "traversal" to the user
  const displayEntityType =
    entityType === AssetType.TRAVERSAL ? 'viewpoint' : entityType;
  const title = t(
    i18nLocale,
    'assetsBrowser:modals.copyCreated.title',
    'Copy created'
  );
  const subtitle = t(
    i18nLocale,
    'assetsBrowser:modals.copyCreated.subtitle',
    { entityType: displayEntityType, copyName },
    'Do you want to open the new {{entityType}} "{{copyName}}"?'
  );
  const openCopyLabel = t(
    i18nLocale,
    'assetsBrowser:modals.copyCreated.confirmButton',
    'Open copy'
  );
  const closeLabel = t(
    i18nLocale,
    'assetsBrowser:modals.copyCreated.cancelButton',
    'Close'
  );

  const trackOpen = () =>
    trackEvent('Copy modal: confirmed open copy', { assetType: entityType });

  // No saved preference: let the user pick where to open the copy.
  if (navigationOptions && navigationOptions.length > 1) {
    return modal<boolean>(resolve => (
      <ModalTemplate
        modalSize={ModalSize.S}
        headerText={title}
        text={subtitle}
        footerContent={
          <Inline gap="small" align="center">
            <SecondaryButton
              data-click-id="cancel-open-copy"
              onClick={() => resolve(false)}
            >
              {closeLabel}
            </SecondaryButton>
            <ButtonWithDropdown
              buttonType={ButtonType.PRIMARY}
              disablePortal
              options={navigationOptions.map(option => ({
                type: DropdownOptionType.BUTTON_OPTION,
                label: option.label,
                description: option.description,
                clickId: 'confirm-open-copy',
                onClick: () => {
                  trackOpen();
                  option.onClick();
                  resolve(true);
                },
              }))}
            >
              {openCopyLabel}
            </ButtonWithDropdown>
          </Inline>
        }
      />
    ));
  }

  return confirm({
    modalSize: ModalSize.S,
    title,
    subtitle,
    confirmButtonClickId: 'confirm-open-copy',
    confirmButtonTitle: openCopyLabel,
    cancelButtonClickId: 'cancel-open-copy',
    cancelButtonTitle: closeLabel,
    onConfirmAsync: () => {
      trackOpen();
      // A single navigation option means the user has a saved preference —
      // open directly there; otherwise fall back to the caller's openCopy.
      if (navigationOptions && navigationOptions.length === 1) {
        navigationOptions[0].onClick();
        return;
      }
      openCopy(copyId);
    },
  });
};
